import { ChangeEvent, useState } from 'react';
import { Field, FieldInputProps, FieldProps, FormikErrors, FormikProps, FormikTouched } from 'formik';
import Input from 'components/common/Input/Input';
import Select, { OptionType } from 'components/common/ReactSelect/Select';
import {
  COUNTRIES_WITH_STATES,
  COUNTRY_OPTIONS,
  EMPLOYEES_OPTIONS,
  MarketingFields,
  STATE_OPTIONS,
} from 'components/standalonePages/common/constants';
import ELEMENT from 'common/element';

type MarketingFormProps = {
  values: MarketingFields;
  handleChange: {
    (e: ChangeEvent<any>): void;
    <T = string | ChangeEvent<any>>(field: T): T extends ChangeEvent<any>
      ? void
      : (e: string | ChangeEvent<any>) => void;
  };
  touched: FormikTouched<MarketingFields>;
  errors: FormikErrors<MarketingFields>;
};

function MarketingForm({ values, handleChange, touched, errors }: MarketingFormProps): JSX.Element {
  const [stateOptions, setStateOptions] = useState<any[]>([]);
  const [countryOptions] = useState(COUNTRY_OPTIONS);

  function isACountryWithAssignedStates(country: string) {
    return Object.values(COUNTRIES_WITH_STATES).includes(country as COUNTRIES_WITH_STATES);
  }

  function handleCountryChange(value: any, form: FormikProps<any>, field: FieldInputProps<any>) {
    form.setFieldValue(field.name, value, true);
    const states = isACountryWithAssignedStates(value) ? STATE_OPTIONS[value] : [];
    const statesMap = states.map((value) => ({
      label: `${value}`,
      value: `${value}`,
    }));
    setStateOptions(statesMap);
  }
  return (
    <>
      <Input
        name="firstName"
        label="First Name *"
        value={values.firstName}
        onChange={handleChange}
        isInvalid={touched.firstName && !!errors.firstName}
        errorMessage={errors.firstName}
        testId={ELEMENT.marketingFormFirstNameInput}
      />
      <Input
        name="lastName"
        label="Last Name *"
        value={values.lastName}
        onChange={handleChange}
        isInvalid={touched.lastName && !!errors.lastName}
        errorMessage={errors.lastName}
        testId={ELEMENT.marketingFormLastNameInput}
      />
      <Input
        type="email"
        name="email"
        label="Business Email *"
        value={values.email}
        onChange={handleChange}
        isInvalid={touched.email && !!errors.email}
        errorMessage={errors.email}
        testId={ELEMENT.marketingFormEmailInput}
      />
      <Input
        name="company"
        label="Company *"
        value={values.company}
        onChange={handleChange}
        isInvalid={touched.company && !!errors.company}
        errorMessage={errors.company}
        testId={ELEMENT.marketingFormCompanyInput}
      />
      <Input
        name="phoneNumber"
        label="Phone *"
        value={values.phoneNumber}
        onChange={handleChange}
        isInvalid={touched.phoneNumber && !!errors.phoneNumber}
        errorMessage={errors.phoneNumber}
        testId={ELEMENT.marketingFormPhoneInput}
      />
      <Field name="numberOfEmployees">
        {({ form, field }: FieldProps) => (
          <div className="form-group">
            <label className="form-label">Employees *</label>
            <Select
              idContainer="number-of-employees"
              defaultValue={{ label: 'Select...', value: '' }}
              options={EMPLOYEES_OPTIONS}
              onChange={(selectedOption) => {
                const { value } = selectedOption as OptionType;
                form.setFieldValue(field.name, value, true);
              }}
              validationErrorMessage={
                form.touched.numberOfEmployees ? (form.errors.numberOfEmployees as string) : undefined
              }
            />
          </div>
        )}
      </Field>
      <Field name="country">
        {({ form, field }: FieldProps) => (
          <div className="form-group">
            <label className="form-label">Country *</label>
            <Select
              idContainer="sign-up-country"
              isSearchable
              defaultValue={{ label: 'Select...', value: '' }}
              options={countryOptions}
              onChange={(selectedOption) => {
                const { value } = selectedOption as OptionType;
                handleCountryChange(value, form, field);
              }}
              validationErrorMessage={form.touched.country ? (form.errors.country as string) : undefined}
            />
          </div>
        )}
      </Field>
      {stateOptions.length > 0 && (
        <Field name="state">
          {({ form, field }: FieldProps) => (
            <div className="form-group">
              <label className="form-label">State *</label>
              <Select
                idContainer="sign-up-state"
                isSearchable
                defaultValue={{ label: 'Select...', value: '' }}
                options={stateOptions}
                onChange={(selectedOption) => {
                  const { value } = selectedOption as OptionType;
                  form.setFieldValue(field.name, value, true);
                }}
                validationErrorMessage={form.touched.state ? (form.errors.state as string) : undefined}
              />
            </div>
          )}
        </Field>
      )}
    </>
  );
}

export default MarketingForm;
